import { debounce, get, isEmpty } from 'radash';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import useRealTimeMessaging from '@/hooks/useRealtimeMessaging';
import { useTranslation } from '@/hooks/useTranslation';
import { useUser } from '@/hooks/useUser';
import { logger } from '@/services/logger';
import { RealTimeMessagingActions, RealTimeMessagingEvents } from '@/types/RealTimeMessagingEvents';

import { useDirtyFields } from './useDirtyFields';

const useItemChangedRealTimeMessaging = ({ primaryKey, onReloadDialog}: any) => {
    const { t } = useTranslation();
    const { isDirty } = useDirtyFields();
    const { user } = useUser();
    const { watch } = useFormContext();

    const { subscribe, unsubscribe, broadcast, isRegistered} = useRealTimeMessaging({
        eventName: RealTimeMessagingEvents.ITEM_CHANGED_EVENT,
        identifier: primaryKey,
        eventHandler: (data: any) => {
            if (data.typeOfChange === 'updated' && data.primaryKeys.includes(primaryKey) && data.userId !== user?.userId) {
                const message = t( (isDirty && data.userId) ? 'ITEM.UPDATED.UNSAVED_CHANGES' : data.userId ? 'ITEM.UPDATED.NO_CHANGES' : 'ITEM.UPDATED.NO_CHANGES_ANONYMOUS', {
                    user: data.userId
                });

                onReloadDialog(message, true);
            }
        }
    });

    useEffect(() => {

        if (!primaryKey || isEmpty(primaryKey)) {
            return;
        }

        const itemChangedEventRegistered = isRegistered(RealTimeMessagingEvents.ITEM_CHANGED_EVENT);
        if (!itemChangedEventRegistered) {
            subscribe(true);
        }

        const onItemChange = debounce({ delay: 500 }, (item, { name: attributeName, type }) => {
            if (!type) return;

            logger.debug('Item changed listener:', item, attributeName, type);

            broadcast({
                action: RealTimeMessagingActions.UNREGISTERED,
                item: { [attributeName]: get(item, attributeName) || null },
                userId: user?.userId,
                id: user?.id
            });
        });

        const itemChangesSubscription = watch(onItemChange);

        return () => itemChangesSubscription.unsubscribe();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [primaryKey, watch, subscribe, unsubscribe, broadcast]);

    return null;
};
export {useItemChangedRealTimeMessaging};
